import { BreakpointObserver } from "@angular/cdk/layout";
import { isPlatformBrowser, CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, inject, OnInit, PLATFORM_ID, signal, computed, DestroyRef } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router, RouterModule } from "@angular/router";
import { Store } from "@ngrx/store";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { identity } from 'rxjs';
import { delay, filter, map, startWith } from 'rxjs/operators';
import { User } from "src/app/models/user/user.model";
import { AssetSearchModalService } from "src/app/shared/services/asset-search-modal.service";
import { AuthService } from "src/app/shared/services/auth.service";
import { SubscriptionService } from "src/app/shared/services/subscription.service";
import { TypesenseService } from "src/app/shared/services/typesense.service";
import { UserApiService } from "src/app/shared/services/user-api.service";
import { restoreSidebarVisibility } from "src/app/stores/sidebar/sidebar.actions";
import { HeaderUserNotificationsComponent } from "../header-user-notifications/header-user-notifications.component";
import { UserDropdownComponent } from "../../new-header/user-dropdown/user-dropdown.component";

import { HEADER_NAV_ITEMS, MenuItem } from "../header-links";

@Component({
   selector: 'app-top-header',
   templateUrl: './top-header.component.html',
   styleUrls: ['./top-header.component.scss'],
   standalone: true,
   imports: [
      CommonModule,
      RouterModule,
      NgbModule,
      HeaderUserNotificationsComponent,
      UserDropdownComponent
   ],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class TopHeaderComponent implements OnInit {
   private readonly store = inject(Store);
   private readonly observer = inject(BreakpointObserver);
   private readonly auth = inject(AuthService);
   private readonly userApiService = inject(UserApiService);
   private readonly typesenseService = inject(TypesenseService);
   private readonly assetSearchModal = inject(AssetSearchModalService);
   private readonly router = inject(Router);
   private readonly platformId = inject(PLATFORM_ID);
   private readonly destroyRef = inject(DestroyRef);

   private readonly isBrowser = isPlatformBrowser(this.platformId);

   // Breakpoints as signals
   private readonly breakpoints = toSignal(this.observer.observe(['(max-width: 992px)', '(max-width: 1280px)']));
   readonly isMobile = computed(() => this.breakpoints()?.breakpoints['(max-width: 992px)'] ?? false);
   readonly isXlOrSmall = computed(() => this.breakpoints()?.breakpoints['(max-width: 1280px)'] ?? false);

   // Routing state
   readonly isInvestingPlatform = toSignal(
      this.router.events.pipe(
         filter(e => e instanceof NavigationEnd),
         map(() => this.router.url.includes('halal-investing-platform')),
         startWith(this.router.url.includes('halal-investing-platform')),
         this.isBrowser ? delay(0) : identity
      ),
      { initialValue: this.router.url.includes('halal-investing-platform') }
   );

   // User and Auth state
   readonly user = signal<User | null>(null);
   readonly userLoading = signal(false);
   readonly userError = signal<string | null>(null);
   private userFetched = false;

   readonly subscriptionData = toSignal(SubscriptionService.subscriptionData.pipe(this.isBrowser ? delay(0) : identity));
   readonly isLoggedIn = toSignal(this.auth.isLoggedIn$.pipe(this.isBrowser ? delay(0) : identity), { initialValue: this.auth.isLoggedIn() });
   readonly trialNotClaimed = toSignal(this.auth.isTrialClaimed$.pipe(map(claimed => !claimed), this.isBrowser ? delay(0) : identity), { initialValue: true });

   readonly userRole = computed(() => this.subscriptionData()?.role ?? null);
   readonly userNewRole = computed(() => this.subscriptionData()?.newRole ?? null);
   readonly showQuestionnaireReward = signal(false);

   readonly navItems = HEADER_NAV_ITEMS;

   constructor() {
      // Manage user data fetching side-effect
      SubscriptionService.subscriptionData
         .pipe(takeUntilDestroyed())
         .subscribe(() => {
            if (!this.isBrowser) return;

            // Defer signal writes to avoid NG0600 during reactive context synchronization
            setTimeout(() => {
               const loggedIn = this.isLoggedIn();
               if (loggedIn && !this.userFetched) {
                  this.userFetched = true;
                  this.getUser();
               }
               if (!loggedIn) {
                  this.userFetched = false;
                  this.user.set(null);
               }
            }, 0);
         });
   }

   ngOnInit(): void {
      this.typesenseService.getConfigData()
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe(config => {
            if (!this.isBrowser) return;
            
            setTimeout(() => {
               this.showQuestionnaireReward.set(config.prod?.questionnaire_reward ?? false);
            }, 0);
         });
   }

   openSidebar(): void {
      this.store.dispatch(restoreSidebarVisibility());
   }

   openSearch(): void {
      this.assetSearchModal.open();
   }

   onSignUpClick(): void {
      try {
         const urlPath = (this.router.url || '').split('?')[0];
         const segments = urlPath.split('/').filter(s => s);
         const mainSegment = segments.length > 0 ? String(segments[0]).toLowerCase() : '';

         const locationMap: Record<string, string> = {
            '': 'Home',
            'pricing': 'Pricing',
            'stock-screener': 'Stock Screener',
            'etf-screener': 'ETF Screener',
            'portfolio-dashboard': 'Portfolio dashboard',
            'stock': 'Stock Details',
            'etf': 'ETF Details',
            'purification': 'Purification Calculator',
            'zakat-calculator': 'Zakat Calculator',
            'halal-investing-platform': 'Investing Platform',
            'for-business': 'For Business',
            'fundraising-advisory-services': 'Fundraising Advisory Services',
            'about-us': 'About Us',
            'screening-methodology': 'Screening Methodology',
            'ambassador-program': 'Ambassador Program',
            'investor-relations': 'Investor Relations',
            'trending-halal-stocks': 'Trending Halal Stocks',
            'halal-collection': 'Halal Collection',
            'super-investors': 'Super Investors',
            'public-lists': 'Public Lists',
         };

         let pagePrefix = locationMap[mainSegment];

         if (pagePrefix) {
            this.auth.setSignupSource(`${pagePrefix} Top Nav`);
         } else {
            this.auth.setSignupSource('Top Nav');
         }
      } catch (e) {
         // Fail silently to never block execution
         this.auth.setSignupSource('Top Nav');
      }
   }

   private getUser() {
      if (!this.isBrowser) return;
      
      this.userLoading.set(true);
      this.userError.set(null);

      this.userApiService.fetchUserDetailsAndSave()
         .then((userData: User) => {
            this.user.set(userData);
         })
         .catch(err => {
            console.error(err);
            this.userError.set('Failed to fetch user data');
         })
         .finally(() => {
            this.userLoading.set(false);
         });
   }
}